// The public pages' words in Chinese, English and Japanese. The language follows the browser unless the person picked one;
// the administrator's pages stay in Chinese.

export type Lang = 'zh' | 'en' | 'ja';

const STORAGE = 'otoha-market-lang';
export const LANG_NAMES: Record<Lang, string> = { zh: '中文', en: 'English', ja: '日本語' };
const HTML_LANG: Record<Lang, string> = { zh: 'zh-CN', en: 'en', ja: 'ja' };
const LOCALE: Record<Lang, string> = { zh: 'zh-CN', en: 'en-US', ja: 'ja-JP' };

const WORDS = {
  marketName: { zh: 'Otoha 市场', en: 'Otoha Market', ja: 'Otoha マーケット' },
  market: { zh: '市场', en: 'Market', ja: 'マーケット' },
  intro: {
    zh: '技能教 Otoha 做事的方法，有的会用到需要账号的服务；插件给 Otoha 加上页面或功能；提示词模板是一段常用的话，只有你选用时才放进消息。添加前请看清来源、内容和使用要求。',
    en: 'Skills teach Otoha how to do a task, and some use services that need an account. Plugins add a page or a feature to Otoha. Prompt templates are text you reuse; they go into a message only when you choose one. Check the source, contents and requirements before adding anything.',
    ja: 'スキルは Otoha に作業のやり方を教えるもので、アカウントが必要なサービスを使うものもあります。プラグインは Otoha に画面や機能を追加します。プロンプトテンプレートはよく使う文章で、選んだときだけメッセージに入ります。追加する前に、提供元・内容・必要な環境を確認してください。',
  },
  search: { zh: '搜索名称或简介', en: 'Search names and descriptions', ja: '名前や説明で検索' },
  submit: { zh: '提交', en: 'Submit', ja: '投稿' },
  all: { zh: '全部', en: 'All', ja: 'すべて' },
  skill: { zh: '技能', en: 'Skills', ja: 'スキル' },
  plugin: { zh: '插件', en: 'Plugins', ja: 'プラグイン' },
  prompt: { zh: '提示词模板', en: 'Prompt templates', ja: 'プロンプトテンプレート' },
  skillOne: { zh: '技能', en: 'Skill', ja: 'スキル' },
  pluginOne: { zh: '插件', en: 'Plugin', ja: 'プラグイン' },
  promptOne: { zh: '提示词模板', en: 'Prompt template', ja: 'プロンプトテンプレート' },
  other: { zh: '其他', en: 'Other', ja: 'その他' },
  noMatch: { zh: '没有找到与“{q}”相关的内容', en: 'Nothing matches “{q}”', ja: '「{q}」に一致するものはありません' },
  noMatchHint: { zh: '换个关键词试试。知道合适的公开仓库时，可以提交。', en: 'Try other words. If you know a suitable public repository, you can submit it.', ja: '別の言葉で試してください。適した公開リポジトリがあれば投稿できます。' },
  emptyAll: { zh: '还没有公开的内容', en: 'Nothing is published yet', ja: 'まだ公開されているものはありません' },
  emptyKind: { zh: '这里还没有公开的{kind}', en: 'No {kind} are published yet', ja: '公開されている{kind}はまだありません' },
  emptyHint: { zh: '提交一个公开 GitHub 仓库，审核通过后会显示在这里。', en: 'Submit a public GitHub repository. It appears here once it passes review.', ja: '公開 GitHub リポジトリを投稿すると、審査を通過した後にここに表示されます。' },
  tryLater: { zh: '请稍后再试。', en: 'Please try again later.', ja: 'しばらくしてからもう一度お試しください。' },
  skillsCount: { zh: '做法 {n} 个', en: 'Ways of working: {n}', ja: 'やり方 {n} 件' },
  listSeparator: { zh: '、', en: ', ', ja: '、' },
  needsAccount: { zh: '需要账号', en: 'Needs an account', ja: 'アカウントが必要' },
  runsHere: { zh: '在本机运行', en: 'Runs on this computer', ja: 'このコンピュータで動作' },
  toolPage: { zh: '工具页', en: 'Tool page', ja: 'ツール画面' },
  appFeature: { zh: '使用 Otoha 的功能', en: 'Works with Otoha', ja: 'Otoha の機能を使用' },
  needsHere: { zh: '本机需要 {list}', en: 'Needs {list} here', ja: '{list} が必要' },
  promptLine: { zh: '一段文字，选用时放进消息', en: 'Text you add to a message when you choose it', ja: '選んだときにメッセージに入る文章' },
  back: { zh: '‹ 返回市场', en: '‹ Back to the market', ja: '‹ マーケットに戻る' },
  unavailable: { zh: '这项内容目前不可用', en: 'This is not available', ja: 'これは現在利用できません' },
  unavailableHint: { zh: '它可能已下架，或尚未通过审核。', en: 'It may have been taken down, or it has not passed review yet.', ja: '取り下げられたか、まだ審査を通過していない可能性があります。' },
  backButton: { zh: '返回市场', en: 'Back to the market', ja: 'マーケットに戻る' },
  version: { zh: '版本 {v}', en: 'Version {v}', ja: 'バージョン {v}' },
  published: { zh: '{date} 发布', en: 'Published {date}', ja: '{date} 公開' },
  download: { zh: '下载 {v}', en: 'Download {v}', ja: '{v} をダウンロード' },
  downloading: { zh: '正在下载…', en: 'Downloading…', ja: 'ダウンロード中…' },
  downloadStarted: { zh: '已开始下载 {name}', en: 'Downloading {name}', ja: '{name} のダウンロードを開始しました' },
  downloadFailed: { zh: '下载失败，请稍后再试。', en: 'The download failed. Please try again later.', ja: 'ダウンロードできませんでした。しばらくしてからもう一度お試しください。' },
  source: { zh: '来源', en: 'Source', ja: '提供元' },
  repository: { zh: '仓库', en: 'Repository', ja: 'リポジトリ' },
  folder: { zh: '目录', en: 'Folder', ja: 'フォルダ' },
  commit: { zh: '提交', en: 'Commit', ja: 'コミット' },
  holds: { zh: '包里有什么', en: 'What it contains', ja: '含まれるもの' },
  holdsSkills: { zh: '在对话里用的做法', en: 'Ways of working used in a conversation', ja: '会話で使うやり方' },
  holdsServices: { zh: '连接的服务', en: 'Connected services', ja: '接続するサービス' },
  serviceAccount: { zh: '需要账号', en: 'Needs an account', ja: 'アカウントが必要' },
  serviceLocal: { zh: '在这台电脑上运行', en: 'Runs on this computer', ja: 'このコンピュータで動作' },
  holdsPages: { zh: '自带的页面', en: 'Pages it adds', ja: '追加される画面' },
  pageSummary: { zh: '由连接的服务提供，在右侧「工具」标签页打开', en: 'Provided by the connected service; opens in the Tools tab on the right', ja: '接続したサービスが提供し、右側の「ツール」タブで開きます' },
  holdsFeatures: { zh: '使用 Otoha 的功能', en: 'Otoha features it uses', ja: '使用する Otoha の機能' },
  featureSummary: { zh: '读取 Otoha 里当前对话的统计', en: 'Reads the statistics of the open conversation in Otoha', ja: 'Otoha で開いている会話の統計を読み取ります' },
  holdsNeeds: { zh: '这台电脑需要', en: 'This computer needs', ja: 'このコンピュータに必要なもの' },
  ffmpegDetail: { zh: '需要安装在本机，并且能在命令行里找到', en: 'Installed on this computer and available on the command line', ja: 'このコンピュータにインストールされ、コマンドラインから使えること' },
  holdsNothing: { zh: '这项内容没有单独列出做法、服务或页面。', en: 'It lists no ways of working, services or pages of its own.', ja: 'やり方・サービス・画面は個別に記載されていません。' },
  chatGptNote: { zh: '清单里有 ChatGPT 的连接编号。那些编号不能在 Otoha 里连接，可用的服务以上面列出的为准。', en: 'Its manifest has ChatGPT connector ids. Those cannot be connected in Otoha; the services listed above are the ones you can use.', ja: 'マニフェストに ChatGPT の接続 ID があります。それらは Otoha では接続できません。使えるサービスは上に記載されたものです。' },
  noDescription: { zh: '发布者没有提供更多说明。', en: 'The publisher gives no further description.', ja: '提供元による詳しい説明はありません。' },
  promptText: { zh: '模板正文', en: 'Template text', ja: 'テンプレート本文' },
  promptNote: { zh: '选用这个模板时，这段文字会放进你正在写的消息，你可以先修改再发送。', en: 'When you choose this template, this text goes into the message you are writing. You can edit it before sending.', ja: 'このテンプレートを選ぶと、この文章が作成中のメッセージに入ります。送信する前に編集できます。' },
  file: { zh: '下载文件', en: 'Download', ja: 'ダウンロードファイル' },
  fileName: { zh: '文件', en: 'File', ja: 'ファイル' },
  fileSize: { zh: '大小', en: 'Size', ja: 'サイズ' },
  checksum: { zh: '校验码', en: 'Checksum', ja: 'チェックサム' },
  signedNote: { zh: '下载的文件带有签名。Otoha 会在添加前核对来源和内容是否被改动。', en: 'The download is signed. Otoha checks its source and that nothing was changed before adding it.', ja: 'ダウンロードしたファイルには署名があります。Otoha は追加する前に提供元と改変がないことを確認します。' },
  language: { zh: '语言', en: 'Language', ja: '言語' },
  loading: { zh: '正在加载…', en: 'Loading…', ja: '読み込み中…' },
  loadFailed: { zh: '暂时无法加载', en: 'Could not load this', ja: '読み込めませんでした' },
  retry: { zh: '重试', en: 'Try again', ja: '再試行' },
} as const satisfies Record<string, Record<Lang, string>>;

export type WordKey = keyof typeof WORDS;

const isLang = (value: unknown): value is Lang => value === 'zh' || value === 'en' || value === 'ja';

function detect(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE);
    if (isLang(stored)) return stored;
  } catch {
    // Storage may be unavailable; the browser's language decides.
  }
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag.toLowerCase().split('-')[0];
    if (isLang(base)) return base;
  }
  return 'zh';
}

let current: Lang = detect();
document.documentElement.lang = HTML_LANG[current];

export const lang = (): Lang => current;

export function setLang(next: Lang): void {
  current = next;
  document.documentElement.lang = HTML_LANG[next];
  try {
    localStorage.setItem(STORAGE, next);
  } catch {
    // Not kept; it still applies to this visit.
  }
}

/** The words for `key` in the current language, with `{name}` filled from `values`. Not escaped. */
export function t(key: WordKey, values: Record<string, string | number> = {}): string {
  return WORDS[key][current].replace(/\{(\w+)\}/g, (_match, name: string) => String(values[name] ?? ''));
}

export const fmtDateIn = (iso: string | number) => new Date(iso).toLocaleDateString(LOCALE[current], { year: 'numeric', month: 'long', day: 'numeric' });

export function isLanguage(value: string): value is Lang {
  return isLang(value);
}
